import { getCustomerCount, getCustomerDetail, toggleAiStatus } from '@/apis/customer';
import { EMPTY_AGENT_ID } from '@/constants';
import { ChannelSource } from '@/interfaces/channel';
import {
  ChatAssignCategory,
  ChatAssignTypes,
  CustomerCount,
  SelectedChatCategory,
  StreamItem,
} from '@/interfaces/chat';
import { CustomerInfo } from '@/interfaces/customer';
import { getAgentId } from '@/utils';
import { errorHandler } from '@/utils/request';
import { atom } from 'jotai';

/** 所选分类：根据 url 参数初始化 */
export const chatCategoryAtom = atom<SelectedChatCategory>({} as unknown as SelectedChatCategory);
const initDefaultChatCategoryAtom = atom(null, (get, set) => {
  if (!(get(chatCategoryAtom) as ChatAssignCategory).assignIndex) {
    set(chatCategoryAtom, { type: 'ASSIGN', assignIndex: ChatAssignTypes.MY });
  }
});
/** 所选用户 */
export const chatCustomerAtom = atom<CustomerInfo | undefined>();
export const fetchChatCustomerAtom = atom(
  (get) => get(chatCustomerAtom),
  async (get, set, id?: string) => {
    return getCustomerDetail(id || get(chatCustomerAtom)!.customerId)
      .then((info) => set(chatCustomerAtom, info))
      .catch(errorHandler);
  },
);
/** 定位消息 */
export const chatLocateMsgAtom = atom<string>();

/** 中断 AI */
const aiDisabledAtom = atom(false);
const resetAiDisabledAtom = atom<null, [string], void>(null, (get, set, customerId) => {
  toggleAiStatus(customerId, true).catch(errorHandler);
  set(aiDisabledAtom, false);
});
export const aiInterruptedAtom = atom<boolean, [string, boolean], void>(
  (get) => get(aiDisabledAtom),
  (get, set, customerId, disabled) => {
    const enableAi = !disabled;
    const customer = get(chatCustomerAtom);
    if (customer) {
      set(chatCustomerAtom, { ...customer, enableAi });
    }
    toggleAiStatus(customerId, enableAi)
      .then(() => set(aiDisabledAtom, disabled))
      .catch(errorHandler);
  },
);

/** REVIEW: 更新时间戳 */
export const chatContentInfoTimestamp = atom<number>();
export const chatContentInfoAtom = atom(
  (get) => ({
    locateMsgId: get(chatLocateMsgAtom),
    customer: get(chatCustomerAtom),
    timestamp: get(chatContentInfoTimestamp),
  }),
  (get, set, { customerId, msgId }: { customerId: string; msgId?: string }) => {
    const prevCustomer = get(chatCustomerAtom);

    set(fetchChatCustomerAtom, customerId).then(() => {
      set(chatLocateMsgAtom, msgId);
      set(chatContentInfoTimestamp, Date.now());
      if (prevCustomer && !prevCustomer.enableAi && get(aiInterruptedAtom)) {
        set(resetAiDisabledAtom, prevCustomer.customerId);
      }
    });
  },
);
export const setLocateMesssageAtom = atom(null, (get, set, msgId: string) => {
  set(chatLocateMsgAtom, msgId);
  set(chatContentInfoTimestamp, Date.now());
});

const getAssignIndex = (customer: CustomerInfo) => {
  const currentAgent = getAgentId();

  if (customer.agentId === currentAgent) {
    return ChatAssignTypes.MY;
  }
  if (customer.agentId === EMPTY_AGENT_ID) {
    return ChatAssignTypes.UNKNOWN;
  }
  return ChatAssignTypes.COLL;
};

export const initChatAtom = atom<null, [SelectedChatCategory['type'] | null, string | null], void>(
  null,
  async (get, set, type, id) => {
    if (!type || !id) {
      set(initDefaultChatCategoryAtom);
      return;
    }

    return getCustomerDetail(id)
      .then((customer) => {
        if (customer) {
          set(chatCustomerAtom, customer);
          switch (type) {
            case 'AI':
              set(chatCategoryAtom, { type: 'AI', status: customer.enableAi ? 'open' : 'close' });
              break;
            case 'ACCOUNT':
              set(chatCategoryAtom, {
                type: 'ACCOUNT',
                source: customer.source,
                subSourceId: customer.subSourceId,
              });
              break;
            case 'ASSIGN':
            default:
              set(chatCategoryAtom, { type: 'ASSIGN', assignIndex: getAssignIndex(customer) });
          }
        } else {
          set(initDefaultChatCategoryAtom);
        }
      })
      .catch(errorHandler);
  },
);

/** 消息流 */
export const chatStreamsAtom = atom<StreamItem[]>([]);

/** 计数 */
export const chatCountAtom = atom<CustomerCount>({
  colleagueCount: 0,
  myCount: 0,
  unassignedCount: 0,
  sourceCount: { redbook: 0, douyin: 0 },
  subSourceCount: {},
  enableAiCount: { true: 0, false: 0 },
  archiveCount: { true: 0, false: 0 },
});
export const aiChatCountAtom = atom<CustomerCount['enableAiCount']>(
  (get) => get(chatCountAtom).enableAiCount,
);
export const assignChatCountAtom = atom<Record<ChatAssignTypes, number>>((get) => ({
  [ChatAssignTypes.MY]: get(chatCountAtom).myCount,
  [ChatAssignTypes.COLL]: get(chatCountAtom).colleagueCount,
  [ChatAssignTypes.UNKNOWN]: get(chatCountAtom).unassignedCount,
}));
export const archiveChatCountAtom = atom<CustomerCount['archiveCount']>(
  (get) => get(chatCountAtom).archiveCount,
);
export const accountChatCountAtom = atom<Record<string, number>>(
  (get) => get(chatCountAtom).subSourceCount,
);

export const sourceChatCountAtom = atom<Record<ChannelSource, number>>(
  (get) => get(chatCountAtom).sourceCount,
);

export const refreshChatCountAtom = atom(null, async (get, set) => {
  return getCustomerCount().then((res) => set(chatCountAtom, res));
});
